import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

/* ---------- Icons ---------- */
const Icon = ({ name, className = 'w-5 h-5' }) => {
  const paths = {
    menu: 'M4 7h16M4 12h16M4 17h16',
    close: 'M6 6l12 12M18 6L6 18',
    bell: 'M12 3a5 5 0 00-5 5v3.5L5.5 15h13L17 11.5V8a5 5 0 00-5-5zM10 18a2 2 0 004 0',
    chat: 'M4 5h16v11H8l-4 4V5z',
    chevronRight: 'M9 6l6 6-6 6',
    grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
    user: 'M12 4a4 4 0 100 8 4 4 0 000-8zM4 20a8 8 0 0116 0',
    wallet: 'M3 7h15a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7zM3 7l2-3h11l2 3',
    receipt: 'M6 3h12v18l-3-2-3 2-3-2-3 2V3zM9 8h6M9 12h6',
    card: 'M3 6h18v12H3zM3 10h18',
    inbox: 'M4 4h16v12H8l-4 4V4z',
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d={paths[name] || ''}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

/* ---------- Nav data ---------- */

const MAIN_ITEMS = [
  { to: '/fintech/dashboard',                label: 'Dashboard',     icon: 'grid',    end: true },
  { to: '/fintech/dashboard/profile',        label: 'Profile',       icon: 'user' },
  { to: '/fintech/dashboard/accounts',       label: 'Accounts',      icon: 'wallet' },
  { to: '/fintech/dashboard/transactions',   label: 'Transactions',  icon: 'receipt' },
  { to: '/fintech/dashboard/card-delivery',  label: 'Card Delivery', icon: 'card' },
  { to: '/fintech/dashboard/messages',       label: 'Messages',      icon: 'inbox' },
];

const BOTTOM_ITEMS = [
  { to: '/fintech/dashboard',                label: 'Dash',     icon: 'grid',    end: true },
  { to: '/fintech/dashboard/accounts',       label: 'Accounts', icon: 'wallet' },
  { to: '/fintech/dashboard/transactions',   label: 'Activity', icon: 'receipt' },
  { to: '/fintech/dashboard/card-delivery',  label: 'Card',     icon: 'card' },
  { to: '/fintech/dashboard/messages',       label: 'Inbox',    icon: 'inbox' },
];

/* ---------- Profile helpers ---------- */

const readProfile = () => {
  const name = localStorage.getItem('fintech_name') || '';
  const caseNumber = localStorage.getItem('fintech_caseNumber') || '—';
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((s) => s[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'AH';
  return { name, caseNumber, initials };
};

/* ---------- Top bar ---------- */

const TopBar = ({ onMenu, profile }) => {
  const navigate = useNavigate();
  return (
    <header className="bg-[#0b2740] text-white sticky top-0 z-30">
      <div className="flex items-center justify-between px-4 h-14">
        <button aria-label="Open menu" onClick={onMenu} className="p-2 -ml-2 lg:hidden">
          <Icon name="menu" />
        </button>
        <div className="lg:hidden flex-1 text-center text-[15px] font-bold tracking-[0.2em]">
          FDIC
        </div>
        <div className="hidden lg:block text-[16px] font-bold tracking-[0.2em]">
          FDIC
        </div>
        <button
          aria-label="Account"
          onClick={() => navigate('/fintech/dashboard/profile')}
          className="p-1"
        >
          <span className="w-8 h-8 rounded-full bg-[#c9a227] text-[#0b2740] text-[11px] font-bold flex items-center justify-center">
            {profile.initials}
          </span>
        </button>
      </div>
    </header>
  );
};

/* ---------- Drawer (navy, mobile overlay) ---------- */

const Drawer = ({ open, onClose, profile }) => {
  const navigate = useNavigate();
  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity lg:hidden ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />
      <aside
        className={`fixed top-0 left-0 h-full w-[280px] bg-[#0b2740] text-white z-50 transform transition-transform lg:hidden ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-5 h-14">
          <span className="text-[15px] font-bold tracking-[0.2em]">FDIC</span>
          <button aria-label="Close menu" onClick={onClose} className="p-2 -mr-2">
            <Icon name="close" />
          </button>
        </div>

        <div className="px-5 pb-4 flex items-center gap-3 border-b border-white/10">
          <span className="w-10 h-10 rounded-full bg-[#c9a227] text-[#0b2740] text-[13px] font-bold flex items-center justify-center"
          onClick={() => navigate('/fintech/dashboard/profile')}
          >
            {profile.initials}
          </span>
          <div className="leading-tight">
            <div className="text-[14px] font-semibold">
              {profile.name || 'Account Holder'}
            </div>
            <div className="text-[11px] text-white/60">{profile.caseNumber}</div>
          </div>
        </div>

        <nav className="py-4">
          <div className="px-5 pb-2 text-[10px] tracking-[0.18em] text-white/40 uppercase">
            Main
          </div>
          {MAIN_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onClose}
              className={({ isActive }) =>
                `relative flex items-center gap-3 px-5 py-3 text-[14px] ${
                  isActive ? 'bg-white/5 text-white' : 'text-white/80 hover:bg-white/5'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute left-0 top-0 bottom-0 w-1 bg-[#3b82f6]" />
                  )}
                  <Icon name={item.icon} className="w-5 h-5" />
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          ))}

          <div className="px-5 pt-6 pb-2 text-[10px] tracking-[0.18em] text-white/40 uppercase">
            Services
          </div>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10">
          <button
            onClick={() => navigate('/fintech/login')}
            className="w-full flex items-center gap-3 px-5 py-4 text-[14px] text-white/80 hover:bg-white/5"
          >
            <Icon name="chevronRight" className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>
    </>
  );
};

/* ---------- Desktop sidebar ---------- */

const Sidebar = ({ profile }) => {
  const navigate = useNavigate();
  return (
    <aside className="hidden lg:flex lg:flex-col w-[260px] bg-[#0b2740] text-white shrink-0">
      <div className="px-5 h-14 flex items-center text-[15px] font-bold tracking-[0.2em]">
        FDIC
      </div>

      <div className="px-5 pb-4 flex items-center gap-3 border-b border-white/10">
        <span className="w-10 h-10 rounded-full bg-[#c9a227] text-[#0b2740] text-[13px] font-bold flex items-center justify-center"
        onClick={() => navigate('/fintech/dashboard/profile')}
        >
          {profile.initials}
        </span>
        <div className="leading-tight">
          <div className="text-[14px] font-semibold">
            {profile.name || 'Account Holder'}
          </div>
          <div className="text-[11px] text-white/60">{profile.caseNumber}</div>
        </div>
      </div>

      <nav className="flex-1 py-4 overflow-y-auto">
        <div className="px-5 pb-2 text-[10px] tracking-[0.18em] text-white/40 uppercase">
          Main
        </div>
        {MAIN_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `relative flex items-center gap-3 px-5 py-3 text-[14px] ${
                isActive ? 'bg-white/5 text-white' : 'text-white/80 hover:bg-white/5'
              }`
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <span className="absolute left-0 top-0 bottom-0 w-1 bg-[#3b82f6]" />
                )}
                <Icon name={item.icon} className="w-5 h-5" />
                <span>{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/10">
        <button
          onClick={() => navigate('/login')}
          className="w-full flex items-center gap-3 px-5 py-4 text-[14px] text-white/80 hover:bg-white/5"
        >
          <Icon name="chevronRight" className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </aside>
  );
};

/* ---------- Bottom tab bar (light, outlined active) ---------- */

/* ---------- Bottom tab bar (light, fully outlined active tab) ---------- */

const BottomTabs = () => (
  <nav className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200">
    <div className="flex items-stretch px-2 py-1.5 gap-1">
      {BOTTOM_ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className="flex-1"
        >
          {({ isActive }) => (
            <span
              className={`flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-lg border text-[10px] leading-none transition-colors ${
                isActive
                  ? 'text-[#0b2740] bg-[#fdf8e8]'
                  : 'text-gray-500 border-transparent'
              }`}
              style={isActive ? { borderColor: '#c9a227', borderWidth: 2 } : undefined}
            >
              <Icon name={item.icon} className="w-5 h-5" />
              <span>{item.label}</span>
            </span>
          )}
        </NavLink>
      ))}
    </div>
  </nav>
);

/* ---------- Layout ---------- */

const FintechLayout = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profile, setProfile] = useState(() => readProfile());

  useEffect(() => {
    const sync = () => setProfile(readProfile());
    window.addEventListener('fintech-profile-updated', sync);
    return () => window.removeEventListener('fintech-profile-updated', sync);
  }, []);

  return (
    <div className="min-h-screen bg-[#f4f6f8] flex flex-col lg:flex-row">
      <Sidebar profile={profile} />

      <div className="flex-1 flex flex-col min-w-0">
        <TopBar onMenu={() => setDrawerOpen(true)} profile={profile} />
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          profile={profile}
        />
        <main className="flex-1 pb-20 lg:pb-24">
          <Outlet />
        </main>
      </div>

      <BottomTabs />
    </div>
  );
};

export default FintechLayout;